import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useLogoutAll,
  useRevokeSession,
  useSessions,
  type AuthSession,
} from '@/auth/hooks/use-sessions';
import { Iconify, type IconifyName } from '@/shared/components/iconify';

interface ActiveSessionsPanelProps {
  /** When true, hides the change-password specific footnote so the panel reads
   * standalone in the dedicated Sessions sub-view. */
  hideFootnote?: boolean;
}

export function ActiveSessionsPanel({ hideFootnote = false }: ActiveSessionsPanelProps) {
  const { t } = useTranslation('settings');
  const { data: sessions, isLoading } = useSessions();
  const revoke = useRevokeSession();
  const logoutAll = useLogoutAll();

  const otherSessions = (sessions ?? []).filter((s) => !s.isCurrent);
  const total = (sessions ?? []).length;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: (t) => `${t.shape.borderRadius}px`,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>
            <Iconify icon="solar:monitor-bold" width={16} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
            {t('account.sessions.title')}
          </Typography>
          {total > 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>{total}</Typography>
          )}
        </Stack>
        {otherSessions.length > 0 && (
          <Link
            component="button"
            type="button"
            onClick={() => logoutAll.mutate()}
            sx={(theme) => ({
              fontSize: 12.5,
              fontWeight: 600,
              color: theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            })}
          >
            {t('account.sessions.signOutAll')}
          </Link>
        )}
      </Stack>

      <Stack spacing={1.25}>
        {isLoading && <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>…</Typography>}
        {!isLoading &&
          (sessions ?? []).map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              onRevoke={() => revoke.mutate(s.id)}
              revoking={revoke.isPending && revoke.variables === s.id}
            />
          ))}
        {!isLoading && otherSessions.length === 0 && (sessions ?? []).length === 1 && (
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', pl: 0.5 }}>
            {t('account.sessions.empty')}
          </Typography>
        )}
      </Stack>

      {!hideFootnote && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: 1,
            borderTopColor: 'divider',
            color: 'text.disabled',
          }}
        >
          <Iconify icon="solar:danger-bold" width={14} />
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
            {t('account.sessions.footnote')}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

interface SessionRowProps {
  session: AuthSession;
  onRevoke: () => void;
  revoking: boolean;
}

function SessionRow({ session, onRevoke, revoking }: SessionRowProps) {
  const { t } = useTranslation('settings');
  const icon = deviceIcon(session.deviceType);
  const isActiveNow = isWithinMinutes(session.lastUsedAt, 5);
  const relative = useRelativeTime(session.lastUsedAt);

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={(theme) => ({
          width: 32,
          height: 32,
          borderRadius: (t) => `${t.shape.borderRadius}px`,
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        <Iconify icon={icon} width={16} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {session.deviceName}
          </Typography>
          {session.isCurrent && <Pill tone="success">{t('account.sessions.thisDevice')}</Pill>}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{relative}</Typography>
          {isActiveNow && session.isCurrent && (
            <Typography
              sx={(theme) => ({
                fontSize: 12,
                color: theme.palette.primary.main,
                fontWeight: 600,
              })}
            >
              · {t('account.sessions.activeNow')}
            </Typography>
          )}
        </Stack>
      </Box>
      {!session.isCurrent && (
        <Button
          size="small"
          onClick={onRevoke}
          disabled={revoking}
          sx={{
            fontSize: 12,
            fontWeight: 500,
            borderRadius: (t) => `${t.shape.borderRadius}px`,
            border: 1,
            borderColor: 'divider',
            color: 'text.primary',
            paddingInline: 1.5,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          {t('account.sessions.revoke')}
        </Button>
      )}
    </Stack>
  );
}

function Pill({ tone, children }: { tone: 'success' | 'warning'; children: ReactNode }) {
  return (
    <Typography
      component="span"
      sx={(theme) => {
        const color = tone === 'success' ? theme.palette.primary.main : theme.palette.error.main;
        return {
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          paddingInline: 0.75,
          paddingBlock: 0.25,
          borderRadius: (t) => `${t.shape.borderRadius}px`,
          color,
          backgroundColor: `${color}1F`,
        };
      }}
    >
      {children}
    </Typography>
  );
}

function deviceIcon(type: AuthSession['deviceType']): IconifyName {
  switch (type) {
    case 'ios':
    case 'android':
      return 'solar:smartphone-2-bold';
    case 'web':
      return 'solar:monitor-bold';
    default:
      return 'custom:globe-outline';
  }
}

function isWithinMinutes(iso: string, minutes: number): boolean {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < minutes * 60 * 1000;
}

function useRelativeTime(iso: string): string {
  const { t } = useTranslation('settings');
  return useMemo(() => {
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return '';
    const diffMs = Date.now() - ts;
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return t('account.sessions.relative.justNow');
    if (diffMin < 60) return t('account.sessions.relative.minutesAgo', { count: diffMin });
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return t('account.sessions.relative.hoursAgo', { count: diffHr });
    const diffDay = Math.floor(diffHr / 24);
    return t('account.sessions.relative.daysAgo', { count: diffDay });
  }, [iso, t]);
}
